import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextInput } from "flowbite-react";
import { toast } from "react-toastify";
import {
  HiOutlineTrash,
  HiChevronUp,
  HiChevronDown,
  HiOutlinePlus,
} from "react-icons/hi";
import { CustomButton } from "../../../atoms/CustomButton";
import { ModalShell } from "../../../atoms/ModalShell";
import { CharacterCounter } from "../../../atoms/CharacterCounter";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { useSaveTodoColumns } from "../../../../hooks/todos";
import {
  TodoColumn,
  MAX_TODO_COLUMNS,
  MAX_TODO_COLUMN_LABEL_LENGTH,
} from "../../../../types/TodoTypes";

interface Props {
  show: boolean;
  onClose: () => void;
  boardId: string;
  columns: TodoColumn[];
}

const TodoColumnsConfigModal: React.FC<Props> = ({
  show,
  onClose,
  boardId,
  columns,
}) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const canWrite = permissions.can(Cap.TASKS_BOARD_CONFIG, Level.WRITE);
  const save = useSaveTodoColumns(
    customer_identification,
    user.entity_identification
  );

  const [draft, setDraft] = useState<TodoColumn[]>([]);

  useEffect(() => {
    if (show) setDraft(columns.map((c) => ({ id: c.id, label: c.label })));
  }, [show, columns]);

  const setLabel = (index: number, label: string) =>
    setDraft((d) => d.map((c, i) => (i === index ? { ...c, label } : c)));
  const remove = (index: number) =>
    setDraft((d) => (d.length <= 1 ? d : d.filter((_, i) => i !== index)));
  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= draft.length) return;
    setDraft((d) => {
      const next = [...d];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };
  const add = () => {
    if (draft.length >= MAX_TODO_COLUMNS) return;
    setDraft((d) => [...d, { id: "", label: "" }]);
  };

  const handleSave = () => {
    if (!canWrite) return;
    const cleaned = draft.map((c) => ({ id: c.id, label: c.label.trim() }));
    if (cleaned.some((c) => !c.label)) {
      toast.error(t("todos.columns.labelRequired") as string);
      return;
    }
    save.mutate(
      { boardId, columns: cleaned as TodoColumn[] },
      {
        onSuccess: (res) => {
          const reassigned = res?.data?.element?.reassigned ?? 0;
          toast.success(
            reassigned > 0
              ? (t("todos.columns.savedReassigned", {
                  count: reassigned,
                }) as string)
              : (t("todos.columns.saved") as string)
          );
          onClose();
        },
        onError: (err) => {
          toast.error(
            (err?.response?.data?.message ||
              t("todos.columns.saveError")) as string
          );
        },
      }
    );
  };

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      size="md"
      dismissible
      title={t("todos.columns.title")}
      footer={
        <>
          <CustomButton
            variant="secondary"
            title={t("common.cancel") as string}
            onClick={onClose}
          />
          <CustomButton
            variant="primary"
            isLoading={save.isLoading}
            disabled={save.isLoading || !canWrite}
            title={t("common.saveShort") as string}
            onClick={handleSave}
          />
        </>
      }
    >
      <div className="space-y-2">
        {draft.map((col, index) => (
          <div key={index} className="flex items-start gap-2">
            <div className="flex flex-col">
              <CustomButton
                variant="none"
                size="icon"
                onClick={() => move(index, -1)}
                disabled={index === 0 || !canWrite}
                className="text-gray-400 enabled:hover:text-gray-700"
                icon={<HiChevronUp size={16} />}
                aria-label={t("common.moveUp") as string}
              />
              <CustomButton
                variant="none"
                size="icon"
                onClick={() => move(index, 1)}
                disabled={index === draft.length - 1 || !canWrite}
                className="text-gray-400 enabled:hover:text-gray-700"
                icon={<HiChevronDown size={16} />}
                aria-label={t("common.moveDown") as string}
              />
            </div>
            <div className="flex-1 min-w-0">
              <TextInput
                type="text"
                value={col.label}
                maxLength={MAX_TODO_COLUMN_LABEL_LENGTH}
                disabled={!canWrite}
                placeholder={t("todos.columns.columnPlaceholder") as string}
                onChange={(e) => setLabel(index, e.target.value)}
                className="w-full"
              />
              <CharacterCounter
                currentLength={col.label.length}
                maxLength={MAX_TODO_COLUMN_LABEL_LENGTH}
                className="mt-0.5"
              />
            </div>
            {canWrite && (
              <CustomButton
                variant="none"
                size="icon"
                onClick={() => remove(index)}
                disabled={draft.length <= 1}
                className="text-gray-400 enabled:hover:text-red-600"
                icon={<HiOutlineTrash size={18} />}
                aria-label={t("todos.columns.remove") as string}
              />
            )}
          </div>
        ))}
      </div>

      {canWrite && draft.length < MAX_TODO_COLUMNS && (
        <CustomButton
          variant="none"
          size="sm"
          onClick={add}
          className="mt-3 inline-flex gap-1.5 px-0 text-blue-600 enabled:hover:text-blue-800"
          icon={<HiOutlinePlus size={16} />}
          title={t("common.add") as string}
        />
      )}
    </ModalShell>
  );
};

export default TodoColumnsConfigModal;
